// === MAGICAL ANIMATIONS & INTERACTIVE EFFECTS === //

class MagicalAnimations {
    constructor() {
        this.particles = [];
        this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.animationQueue = [];
        this.activeAnimations = new Set();
        
        this.init();
    }
    
    init() {
        if (this.isReducedMotion) {
            console.log('Reduced motion preference detected - limiting animations');
            return;
        }
        
        this.createFloatingBubbles();
        this.initializeParticleSystem();
        this.setupMagicalCursor();
        this.bindInteractiveEvents();
        this.startAmbientAnimations();
        
        console.log('🪄 Magical animations initialized!');
    }
    
    // === FLOATING BUBBLES === //
    createFloatingBubbles() {
        const bubbleContainer = document.createElement('div');
        bubbleContainer.className = 'bubble-container';
        bubbleContainer.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: -1;
            overflow: hidden;
        `;
        
        document.body.appendChild(bubbleContainer);
        
        // Create bubbles
        for (let i = 0; i < 10; i++) {
            setTimeout(() => {
                this.createBubble(bubbleContainer);
            }, i * 1000);
        }
        
        // Continuously create new bubbles
        setInterval(() => {
            if (bubbleContainer.children.length < 10) {
                this.createBubble(bubbleContainer);
            }
        }, 3000);
    }
    
    createBubble(container) {
        const bubble = document.createElement('div');
        const size = Math.random() * 20 + 10;
        const left = Math.random() * 100;
        const duration = Math.random() * 10 + 15;
        const delay = Math.random() * 5;
        
        bubble.style.cssText = `
            position: absolute;
            width: ${size}px;
            height: ${size}px;
            background: linear-gradient(45deg, rgba(255, 107, 157, 0.3), rgba(168, 85, 247, 0.3));
            border-radius: 50%;
            left: ${left}%;
            bottom: -50px;
            animation: bubble-float ${duration}s linear infinite;
            animation-delay: ${delay}s;
            backdrop-filter: blur(2px);
            border: 1px solid rgba(255, 255, 255, 0.3);
        `;
        
        container.appendChild(bubble);
        
        // Remove bubble after animation
        setTimeout(() => {
            if (bubble.parentNode) {
                bubble.parentNode.removeChild(bubble);
            }
        }, (duration + delay) * 1000);
    }
    
    // === PARTICLE SYSTEM === //
    initializeParticleSystem() {
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d');
        this.canvas.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            pointer-events: none;
            z-index: 1;
            opacity: 0.7;
        `;
        
        document.body.appendChild(this.canvas);
        
        this.resizeCanvas();
        window.addEventListener('resize', () => this.resizeCanvas());
        
        this.startParticleAnimation();
    }
    
    resizeCanvas() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
    }
    
    createParticle(x, y, color = '#FF6B9D') {
        return {
            x: x || Math.random() * this.canvas.width,
            y: y || Math.random() * this.canvas.height,
            vx: (Math.random() - 0.5) * 2,
            vy: (Math.random() - 0.5) * 2,
            size: Math.random() * 3 + 1,
            color: color,
            life: 1,
            decay: Math.random() * 0.02 + 0.005,
            gravity: Math.random() * 0.1 + 0.05
        };
    }
    
    updateParticles() {
        this.particles = this.particles.filter(particle => {
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.vy += particle.gravity;
            particle.life -= particle.decay;
            
            return particle.life > 0;
        });
    }
    
    drawParticles() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        this.particles.forEach(particle => {
            this.ctx.save();
            this.ctx.globalAlpha = particle.life;
            this.ctx.fillStyle = particle.color;
            this.ctx.beginPath();
            this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.restore();
        });
    }
    
    startParticleAnimation() {
        const animate = () => {
            this.updateParticles();
            this.drawParticles();
            requestAnimationFrame(animate);
        };
        
        animate();
    }
    
    // === MAGICAL CURSOR === //
    setupMagicalCursor() {
        let mouseX = 0;
        let mouseY = 0;
        let trail = [];
        
        document.addEventListener('mousemove', (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
            
            // Add to trail
            trail.push({ x: mouseX, y: mouseY, time: Date.now() });
            
            // Limit trail length
            if (trail.length > 20) {
                trail.shift();
            }
            
            // Create sparkle effect occasionally
            if (Math.random() < 0.1) {
                this.createSparkle(mouseX, mouseY);
            }
        });
        
        // Draw cursor trail
        const drawTrail = () => {
            const now = Date.now();
            trail = trail.filter(point => now - point.time < 500);
            
            this.ctx.save();
            this.ctx.globalCompositeOperation = 'lighter';
            
            trail.forEach((point, index) => {
                const age = now - point.time;
                const opacity = 1 - (age / 500);
                const size = 3 * opacity;
                
                this.ctx.globalAlpha = opacity * 0.5;
                this.ctx.fillStyle = '#FF6B9D';
                this.ctx.beginPath();
                this.ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
                this.ctx.fill();
            });
            
            this.ctx.restore();
            requestAnimationFrame(drawTrail);
        };
        
        drawTrail();
    }
    
    createSparkle(x, y) {
        const sparkle = document.createElement('div');
        sparkle.innerHTML = '✨';
        sparkle.style.cssText = `
            position: fixed;
            left: ${x - 10}px;
            top: ${y - 10}px;
            font-size: 20px;
            pointer-events: none;
            z-index: 9999;
            animation: sparkle-fade 1s ease-out forwards;
        `;
        
        document.body.appendChild(sparkle);
        
        setTimeout(() => {
            if (sparkle.parentNode) {
                sparkle.parentNode.removeChild(sparkle);
            }
        }, 1000);
    }
    
    // === INTERACTIVE EVENTS === //
    bindInteractiveEvents() {
        // Button hover effects
        document.addEventListener('mouseenter', (e) => {
            if (!e.target || !e.target.classList) return;
            
            if (e.target.classList.contains('btn')) {
                this.animateButton(e.target, 'enter');
            }
            
            if (e.target.classList.contains('album-card')) {
                this.animateCard(e.target, 'enter');
            }
            
            if (e.target.classList.contains('shape')) {
                this.animateShape(e.target);
            }
        }, true);
        
        document.addEventListener('mouseleave', (e) => {
            if (!e.target || !e.target.classList) return;
            
            if (e.target.classList.contains('btn')) {
                this.animateButton(e.target, 'leave');
            }
            
            if (e.target.classList.contains('album-card')) {
                this.animateCard(e.target, 'leave');
            }
        }, true);
        
        // Click effects
        document.addEventListener('click', (e) => {
            if (e.clientX !== undefined && e.clientY !== undefined) {
                this.createClickRipple(e.clientX, e.clientY);
            }
            
            if (e.target && e.target.classList && e.target.classList.contains('btn')) {
                this.createButtonParticles(e.target);
            }
        });
        
        // Scroll animations
        this.setupScrollAnimations();
    }
    
    animateButton(button, action) {
        if (action === 'enter') {
            button.style.transform = 'translateY(-3px) scale(1.05)';
            button.style.boxShadow = '0 15px 35px rgba(255, 107, 157, 0.4)';
            
            // Create hover particles
            this.createHoverParticles(button);
        } else {
            button.style.transform = 'translateY(0) scale(1)';
            button.style.boxShadow = '';
        }
    }
    
    animateCard(card, action) {
        if (action === 'enter') {
            card.style.transform = 'translateY(-10px) rotateY(5deg)';
            card.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.2)';
            
            // Add floating effect
            card.style.animation = 'gentle-float 3s ease-in-out infinite';
        } else {
            card.style.transform = 'translateY(0) rotateY(0)';
            card.style.boxShadow = '';
            card.style.animation = '';
        }
    }
    
    animateShape(shape) {
        // Create bouncing animation
        shape.style.animation = 'bounce-and-spin 0.8s ease-in-out';
        
        // Reset animation after completion
        setTimeout(() => {
            shape.style.animation = 'float 6s ease-in-out infinite';
        }, 800);
        
        // Create burst of particles
        const rect = shape.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        for (let i = 0; i < 10; i++) {
            const particle = this.createParticle(centerX, centerY, this.getRandomColor());
            particle.vx = (Math.random() - 0.5) * 10;
            particle.vy = (Math.random() - 0.5) * 10;
            this.particles.push(particle);
        }
    }
    
    createClickRipple(x, y) {
        const ripple = document.createElement('div');
        ripple.style.cssText = `
            position: fixed;
            left: ${x - 25}px;
            top: ${y - 25}px;
            width: 50px;
            height: 50px;
            border: 2px solid var(--primary-pink);
            border-radius: 50%;
            pointer-events: none;
            z-index: 9998;
            animation: ripple-expand 0.6s ease-out forwards;
        `;
        
        document.body.appendChild(ripple);
        
        setTimeout(() => {
            if (ripple.parentNode) {
                ripple.parentNode.removeChild(ripple);
            }
        }, 600);
    }
    
    createButtonParticles(button) {
        const rect = button.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Create burst of colorful particles
        for (let i = 0; i < 15; i++) {
            const particle = this.createParticle(centerX, centerY, this.getRandomColor());
            particle.vx = (Math.random() - 0.5) * 8;
            particle.vy = (Math.random() - 0.5) * 8;
            particle.size = Math.random() * 4 + 2;
            this.particles.push(particle);
        }
    }
    
    createHoverParticles(element) {
        const rect = element.getBoundingClientRect();
        
        // Create gentle floating particles around the element
        for (let i = 0; i < 5; i++) {
            const x = rect.left + Math.random() * rect.width;
            const y = rect.top + Math.random() * rect.height;
            const particle = this.createParticle(x, y, '#A855F7');
            particle.vy = -Math.random() * 2 - 1;
            particle.vx = (Math.random() - 0.5) * 2;
            particle.gravity = -0.02;
            this.particles.push(particle);
        }
    }
    
    // === SCROLL ANIMATIONS === //
    setupScrollAnimations() {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };
        
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.triggerScrollAnimation(entry.target);
                }
            });
        }, observerOptions);
        
        // Observe elements with animation classes
        document.querySelectorAll('[data-aos], .scroll-reveal, .animate-on-scroll').forEach(el => {
            observer.observe(el);
        });
    }
    
    triggerScrollAnimation(element) {
        // Add entrance animation
        element.style.opacity = '1';
        element.style.transform = 'translateY(0)';
        
        // Create celebration particles for special sections
        if (element.classList.contains('section-title')) {
            this.createTitleParticles(element);
        }
        
        // Add staggered animation for grid items
        if (element.classList.contains('albums-grid') || element.classList.contains('creations-grid')) {
            this.staggerGridAnimation(element);
        }
    }
    
    createTitleParticles(titleElement) {
        const rect = titleElement.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Create rainbow particles
        const colors = ['#FF6B9D', '#A855F7', '#3B82F6', '#10B981', '#F59E0B', '#F97316'];
        
        for (let i = 0; i < 20; i++) {
            const particle = this.createParticle(
                centerX + (Math.random() - 0.5) * rect.width,
                centerY,
                colors[Math.floor(Math.random() * colors.length)]
            );
            particle.vy = -Math.random() * 5 - 2;
            particle.vx = (Math.random() - 0.5) * 6;
            particle.size = Math.random() * 5 + 2;
            this.particles.push(particle);
        }
    }
    
    staggerGridAnimation(gridElement) {
        const items = gridElement.children;
        
        Array.from(items).forEach((item, index) => {
            setTimeout(() => {
                item.style.opacity = '1';
                item.style.transform = 'translateY(0) scale(1)';
                
                // Add a little bounce effect
                item.style.animation = 'bounce-in 0.6s ease-out';
            }, index * 100);
        });
    }
    
    // === AMBIENT ANIMATIONS === //
    startAmbientAnimations() {
        // Floating shapes periodic animation
        setInterval(() => {
            document.querySelectorAll('.shape').forEach(shape => {
                if (Math.random() < 0.3) {
                    this.addTemporaryGlow(shape);
                }
            });
        }, 5000);
        
        // Random sparkles across the page
        setInterval(() => {
            if (Math.random() < 0.1) {
                const x = Math.random() * window.innerWidth;
                const y = Math.random() * window.innerHeight;
                this.createSparkle(x, y);
            }
        }, 3000);
        
        // Gentle particle rain
        setInterval(() => {
            if (this.particles.length < 50) {
                const particle = this.createParticle(
                    Math.random() * this.canvas.width,
                    -10,
                    this.getRandomColor()
                );
                particle.vy = Math.random() * 2 + 1;
                particle.vx = (Math.random() - 0.5) * 1;
                this.particles.push(particle);
            }
        }, 2000);
    }
    
    addTemporaryGlow(element) {
        const originalBoxShadow = element.style.boxShadow;
        element.style.boxShadow = '0 0 20px rgba(255, 107, 157, 0.8)';
        element.style.transform = 'scale(1.1)';
        
        setTimeout(() => {
            element.style.boxShadow = originalBoxShadow;
            element.style.transform = 'scale(1)';
        }, 1000);
    }
    
    // === SPECIAL EFFECTS === //
    celebrationBurst(x, y) {
        // Create a burst of colorful particles
        for (let i = 0; i < 30; i++) {
            const particle = this.createParticle(x, y, this.getRandomColor());
            const angle = (Math.PI * 2 * i) / 30;
            const speed = Math.random() * 8 + 5;
            particle.vx = Math.cos(angle) * speed;
            particle.vy = Math.sin(angle) * speed;
            particle.size = Math.random() * 6 + 3;
            particle.gravity = 0.2;
            this.particles.push(particle);
        }
        
        // Add screen flash
        this.flashScreen();
    }
    
    flashScreen() {
        const flash = document.createElement('div');
        flash.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(255, 255, 255, 0.3);
            pointer-events: none;
            z-index: 9999;
            animation: flash-fade 0.3s ease-out forwards;
        `;
        
        document.body.appendChild(flash);
        
        setTimeout(() => {
            if (flash.parentNode) {
                flash.parentNode.removeChild(flash);
            }
        }, 300);
    }
    
    rainbowTrail(element) {
        const colors = ['#FF6B9D', '#A855F7', '#3B82F6', '#10B981', '#F59E0B', '#F97316'];
        let colorIndex = 0;
        
        const trail = setInterval(() => {
            element.style.boxShadow = `0 0 30px ${colors[colorIndex]}`;
            colorIndex = (colorIndex + 1) % colors.length;
        }, 200);
        
        setTimeout(() => {
            clearInterval(trail);
            element.style.boxShadow = '';
        }, 2000);
    }
    
    // === UTILITY METHODS === //
    getRandomColor() {
        const colors = ['#FF6B9D', '#A855F7', '#3B82F6', '#10B981', '#F59E0B', '#F97316'];
        return colors[Math.floor(Math.random() * colors.length)];
    }
    
    cleanup() {
        if (this.canvas) {
            this.canvas.remove();
        }
        
        document.querySelectorAll('.bubble-container').forEach(container => {
            container.remove();
        });
        
        this.particles = [];
        this.activeAnimations.clear();
    }
    
    // === PUBLIC API === //
    triggerCelebration(x = window.innerWidth / 2, y = window.innerHeight / 2) {
        this.celebrationBurst(x, y);
    }
    
    addRainbowEffect(element) {
        this.rainbowTrail(element);
    }
    
    createCustomParticles(x, y, count = 10, color = null) {
        for (let i = 0; i < count; i++) {
            const particle = this.createParticle(x, y, color || this.getRandomColor());
            particle.vx = (Math.random() - 0.5) * 10;
            particle.vy = (Math.random() - 0.5) * 10;
            this.particles.push(particle);
        }
    }
}

// Additional animation CSS
const animationStyles = `
    @keyframes sparkle-fade {
        0% {
            opacity: 1;
            transform: scale(0) rotate(0deg);
        }
        50% {
            opacity: 1;
            transform: scale(1.2) rotate(180deg);
        }
        100% {
            opacity: 0;
            transform: scale(0) rotate(360deg);
        }
    }
    
    @keyframes ripple-expand {
        0% {
            transform: scale(0);
            opacity: 1;
        }
        100% {
            transform: scale(4);
            opacity: 0;
        }
    }
    
    @keyframes bounce-and-spin {
        0% {
            transform: scale(1) rotate(0deg);
        }
        25% {
            transform: scale(1.2) rotate(90deg);
        }
        50% {
            transform: scale(1.4) rotate(180deg);
        }
        75% {
            transform: scale(1.2) rotate(270deg);
        }
        100% {
            transform: scale(1) rotate(360deg);
        }
    }
    
    @keyframes gentle-float {
        0%, 100% {
            transform: translateY(0) rotateY(0deg);
        }
        50% {
            transform: translateY(-5px) rotateY(2deg);
        }
    }
    
    @keyframes bounce-in {
        0% {
            opacity: 0;
            transform: scale(0.3) translateY(30px);
        }
        50% {
            opacity: 1;
            transform: scale(1.05) translateY(-10px);
        }
        70% {
            transform: scale(0.9) translateY(5px);
        }
        100% {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }
    
    @keyframes flash-fade {
        0% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }
    
    .animate-on-scroll {
        opacity: 0;
        transform: translateY(30px);
        transition: all 0.6s ease;
    }
    
    .animate-on-scroll.revealed {
        opacity: 1;
        transform: translateY(0);
    }
    
    /* Bubble animation */
    @keyframes bubble-float {
        0% {
            transform: translateY(100vh) scale(0) rotate(0deg);
            opacity: 0;
        }
        10% {
            opacity: 1;
        }
        90% {
            opacity: 1;
        }
        100% {
            transform: translateY(-100vh) scale(1) rotate(360deg);
            opacity: 0;
        }
    }
    
    /* Performance optimizations */
    .particle-canvas {
        will-change: contents;
    }
    
    .floating-element {
        will-change: transform;
    }
    
    /* Accessibility: Respect reduced motion preference */
    @media (prefers-reduced-motion: reduce) {
        .bubble-container,
        .particle-canvas {
            display: none !important;
        }
        
        * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
        }
    }
`;

// Add animation styles to the page
const animationStyleSheet = document.createElement('style');
animationStyleSheet.textContent = animationStyles;
document.head.appendChild(animationStyleSheet);

// Initialize animations when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.magicalAnimations = new MagicalAnimations();
});

// Clean up on page unload
window.addEventListener('beforeunload', () => {
    if (window.magicalAnimations) {
        window.magicalAnimations.cleanup();
    }
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = MagicalAnimations;
}
